// --- Framework
import React from 'react';
import PropTypes from 'prop-types';

// --- External tools
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';

// --- IO
import API from 'io/API';

// --- Logic
import TranslationKey from 'logic/enums/TranslationKey';
import { isValidEmailAddress } from 'logic/stringOperations';

// --- External components
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';

// --- Style
const styles = theme => ({
  form: {},
});

const formRules = Object.freeze({
  email: [
    value => !!value || { i18nKey: TranslationKey.validation_email_empty },
    value => isValidEmailAddress(value) || { i18nKey: TranslationKey.validation_email_invalid },
  ],
  password1: [
    value => !!value || { i18nKey: TranslationKey.validation_password_empty },
    value => value.length >= 4 || { i18nKey: TranslationKey.validation_string_too_short, values: { min_length: 4 } },
  ],
  password2: [
    value => !!value || { i18nKey: TranslationKey.validation_password_empty },
  ],
});

const defaultState = Object.freeze({
  email: '',
  token: '',
  password1: '',
  password2: '',
  errors: {
    email: [],
    password1: [],
    password2: [],
  },
  requestError: null,
  isFormDisabled: false,
  infoMessage: null,
});

class ResetPassword extends React.Component {
  constructor(props) {
    super(props);

    this.state = { ...defaultState };

    this.onSubmit = this.onSubmit.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.switchToSignIn = this.switchToSignIn.bind(this);
  }

  componentDidMount() {
    try {
      const params = new URLSearchParams(this.props.location && this.props.location.search ? this.props.location.search : '');
      const token = params.get('token') || '';
      const email = params.get('email') || '';
      if (token || email) {
        this.setState((prev) => ({
          ...prev,
          email,
          token,
        }));
      }
    } catch (e) {
      // ignore parsing issues
    }
  }

  async validateForm() {
    let hasErrors = false;

    await this.setState((currentState) => {
      const errors = Object.keys(formRules).reduce((result, field) => {
        result[field] = formRules[field]
          .map(rule => rule(currentState[field]))
          .filter(ruleResult => ruleResult !== true);

        if (result[field].length > 0)
          hasErrors = true;

        return result;
      }, {});

      // Confirm password matching check
      if (currentState.password1 !== currentState.password2) {
        hasErrors = true;
        errors.password2 = errors.password2.concat({ i18nKey: TranslationKey.error_request_failed_with_error_in_response, values: { error: 'Wachtwoorden komen niet overeen' } });
      }

      return { errors };
    });

    return hasErrors;
  }

  async onSubmit(event) {
    if (event != null)
      event.preventDefault();

    await this.setState(currentState => ({
      requestError: null,
      infoMessage: null,
      isFormDisabled: true,
      email: currentState.email.toLowerCase(),
    }));

    const {
      state: {
        token,
        email,
        password1,
        password2,
      },
    } = this;

    const hasErrors = await this.validateForm();

    // Invalid fields.
    if (hasErrors) {
      await this.setState({ isFormDisabled: false });
      return;
    }

    try {
      const response = await API.postPasswordResetConfirm(token, email, password1, password2);
      if (response && (response.status === 200 || response.status === 201)) {
        const msg = response.data && (response.data.message || response.data.detail);
        await this.setState({
          isFormDisabled: false,
          infoMessage: { message: msg || 'Wachtwoord succesvol ingesteld. U kunt nu inloggen.' },
          requestError: null,
        });
        return;
      }
      await this.setState({
        isFormDisabled: false,
        requestError: { message: 'Kan wachtwoord niet instellen. Probeer het later opnieuw.' },
        infoMessage: null,
      });
    } catch (error) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.detail || error.message;
      await this.setState({
        isFormDisabled: false,
        requestError: { message: serverMessage || 'Kan wachtwoord niet instellen. Probeer het later opnieuw.' },
        infoMessage: null,
      });
    }
  }

  switchToSignIn() {
    const { history } = this.props;
    history.push('/sign-in');
  }

  render() {
    const {
      props: { t, classes },
      state: { 
        errors, requestError, isFormDisabled, infoMessage
       },
    } = this;

    return (
      <Container
        disableGutters
        className="page"
      >
        <Typography
          variant="h5"
          component="h2"
          className="pageTitle"
          style={{
            textAlign: 'center',
            marginBottom: '16px',
          }}
        >
          {/* Wachtwoord opnieuw instellen */}
          Wachtwoord opnieuw instellen
        </Typography>
        <Container
          noValidate
          maxWidth="xs"
          disableGutters
          component="form"
          onSubmit={this.onSubmit}
          className="pageContainer"
        >
          <Grid
            container
            spacing={2}
            justify="center"
            direction="column"
            className="content"
            alignItems="stretch"
          >
            <Grid item>
              <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center' }}>
                Voer hieronder uw nieuwe wachtwoord twee keer in.
              </Typography>
            </Grid>

            <Grid item>
              <TextField
                required
                fullWidth
                type="email"
                size="small"
                variant="outlined"
                disabled={isFormDisabled}
                value={this.state.email}
                label={t(TranslationKey.email)}
                error={errors.email.length > 0}
                helperText={errors.email.length > 0 ? t(errors.email[0].i18nKey, errors.email[0].values) : null}
                onChange={({ target: { value } }) => this.setState((previousState) => {
                  if (errors.email.length > 0)
                    return { email: value, errors: { ...previousState.errors, email: [] } };
                  return { email: value };
                })}
              />
            </Grid>

            <Grid item>
              <TextField
                required
                fullWidth
                size="small"
                type="password"
                id="new-password"
                variant="outlined"
                disabled={isFormDisabled}
                autoComplete="new-password"
                value={this.state.password1}
                label="Nieuw wachtwoord"
                error={errors.password1.length > 0}
                helperText={errors.password1.length > 0 ? t(errors.password1[0].i18nKey, errors.password1[0].values) : null}
                onChange={({ target: { value } }) => this.setState((previousState) => {
                  if (errors.password1.length > 0) {
                    return { password1: value, errors: { ...previousState.errors, password1: [] } };
                  }
                  return { password1: value };
                })}
              />
            </Grid>

            <Grid item>
              <TextField
                required
                fullWidth
                size="small"
                type="password"
                id="confirm-password"
                variant="outlined"
                disabled={isFormDisabled}
                autoComplete="new-password"
                value={this.state.password2}
                label="Bevestig nieuw wachtwoord"
                error={errors.password2.length > 0}
                helperText={errors.password2.length > 0 ? (errors.password2[0].message || t(errors.password2[0].i18nKey, errors.password2[0].values)) : null}
                onChange={({ target: { value } }) => this.setState((previousState) => {
                  if (errors.password2.length > 0) {
                    return { password2: value, errors: { ...previousState.errors, password2: [] } };
                  }
                  return { password2: value };
                })}
              />
            </Grid>

            {requestError && (
              <Grid item>
                <Typography color="error" variant="body2">
                  {requestError.message || t(requestError.i18nKey, requestError.values)}
                </Typography>
              </Grid>
            )}

            {infoMessage && (
              <>
                <Grid item>
                  <Typography color="primary" variant="body2">
                    {infoMessage.message || t(infoMessage.i18nKey, infoMessage.values)}
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    fullWidth
                    size="large"
                    type="button"
                    color="primary"
                    disableElevation
                    variant="contained"
                    disabled={false}
                    onClick={this.switchToSignIn}
                  >
                    {t(TranslationKey.to_sign_in)}
                  </Button>
                </Grid>
              </>
            )}

            {!infoMessage && (
              <Grid item>
                <Button
                  fullWidth
                  size="large"
                  type="submit"
                  color="primary"
                  disableElevation
                  variant="contained"
                  disabled={isFormDisabled}
                >
                  Bevestigen
                </Button>
              </Grid>
            )}
          </Grid>
        </Container>
      </Container>
    );
  }
}

ResetPassword.propTypes = {
  t: PropTypes.func,
  i18n: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object,
  classes: PropTypes.object.isRequired,
};

export default compose(
  withTranslation(),
  withStyles(styles),
)(ResetPassword);
